import {useEffect} from "react";
import {readLocalStorageValue} from "@mantine/hooks";
import {chatApi} from "./chatApi";
import {getApiLanguageCode} from "../../types";

/**
 * MIGRATION HOOK: Migrate localStorage language preference to backend settings API
 *
 * This hook migrates users who previously set their language preference via the UI
 * (stored only in localStorage) to the new backend settings API, so their AI responses
 * use their preferred language.
 *
 * TODO: Remove this hook after sufficient time has passed (e.g., after 2025-03-01)
 */
export function useMigrateLanguageSettings(
    initData: ReturnType<typeof chatApi.endpoints.getInit.useQuery>
) {
    const [updateSettings] = chatApi.endpoints.updateSettings.useMutation();

    const langSettingsMigrated = readLocalStorageValue<boolean>({
        key: "langSettingsMigrated",
        defaultValue: false,
    });

    useEffect(() => {
        if (langSettingsMigrated) return;
        if (!initData.isSuccess) return;

        const userPreferredLanguage = initData.data?.settings?.preferredLanguage;

        // Only migrate if backend has no preference set
        if (userPreferredLanguage) {
            localStorage.setItem("langSettingsMigrated", "true");
            return;
        }

        const localStorageLanguage = readLocalStorageValue<string>({
            key: "language",
        });

        // Migrate any localStorage language preference to backend
        if (localStorageLanguage) {
            updateSettings({
                preferredLanguage: getApiLanguageCode(localStorageLanguage),
            });
        }

        localStorage.setItem("langSettingsMigrated", "true");
    }, [initData.isSuccess, langSettingsMigrated]);
}
